import { Button, EmptyState, FormRow, FormSection, Pill, formatDate, formatMoney, Skeleton } from "@omnabase/ui";
import type { BillingCustomerRead, InvoiceRow, InvoicesRead } from "@omnabase/shared";
import { useFetch } from "../../lib/inbox";
import { useStripePages } from "../../lib/billing";
import { useSession } from "../../shell/store";

// Settings › Billing's two Stripe tabs (M7 round four; brief task 5): Payment details shows the company lines Stripe's customer holds
// (what Checkout collected) with the way to Stripe's Portal for the card; Invoices lists Stripe's invoices with their hosted page and
// PDF. Before the first payment each tab is an empty state saying where the data comes from.
const STATUS_WORDS: Record<InvoiceRow["status"], string> = { paid: "Paid", open: "Open", void: "Void", uncollectible: "Uncollectible" };
const countryName = (code: string) => { try { return new Intl.DisplayNames(["en"], { type: "region" }).of(code) ?? code; } catch { return code; } };
const amount = (r: InvoiceRow) => formatMoney(r.amountCents / 100, r.currency.toUpperCase(), r.amountCents % 100 !== 0);

export function PaymentDetailsTab() {
  const { workspace } = useSession();
  const { busy, portal } = useStripePages();
  const read = useFetch<BillingCustomerRead>(`/api/v1/w/${workspace.slug}/billing/customer`);
  if (!read.data) return read.error ? <EmptyState icon="card" title="Payment details could not be read" text={read.error} /> : <TabSkeleton />;
  const c = read.data.customer;
  if (!c) return <EmptyState icon="card" title="No payment details yet" text="The card, the company on the invoice and the VAT ID are entered on Stripe's checkout page with the first payment. Choose a plan under Overview › Change plan." />;
  const dash = (v: string | null) => v ?? "Not given";
  return (
    <FormSection title="Payment and invoice details" intro="Stripe keeps the card and the invoice address; OmnaBase never sees the card number. Change them on Stripe's page.">
      <FormRow label="Company on the invoice">{dash(c.name)}</FormRow>
      <FormRow label="Invoices go to">{dash(c.email)}</FormRow>
      <FormRow label="Country">{c.country ? countryName(c.country) : "Not given"}</FormRow>
      <FormRow label="VAT ID" help="With a valid EU VAT ID outside Austria no VAT is charged (reverse charge).">{dash(c.vatId)}</FormRow>
      <FormRow label="Card and address" help="The card, the address, the VAT ID and the plan are changed on Stripe's page.">
        <Button icon="card" disabled={busy === "portal"} onClick={portal}>Manage on Stripe</Button>
      </FormRow>
    </FormSection>
  );
}

export function InvoicesTab() {
  const { workspace } = useSession();
  const read = useFetch<InvoicesRead>(`/api/v1/w/${workspace.slug}/billing/invoices`);
  if (!read.data) return read.error ? <EmptyState icon="card" title="The invoices could not be read" text={read.error} /> : <TabSkeleton />;
  if (read.data.rows.length === 0) return <EmptyState icon="card" title="No invoices yet" text="Each month's plan gets an invoice here from the first payment on." />;
  return (
    <FormSection title="Invoices" intro="Stripe writes one for each month of the plan and sends it to the address under Payment details. Newest first.">
      <div className="ob-dtable-card">
        <table className="ob-dtable" aria-label="Invoices">
          <thead><tr><th>Invoice</th><th>Date</th><th className="ob-dtable__num">Amount</th><th className="ob-dtable__col--phone">Status</th><th /></tr></thead>
          <tbody>{read.data.rows.map((r) => <InvoiceLine key={r.id} r={r} />)}</tbody>
        </table>
      </div>
    </FormSection>
  );
}

function InvoiceLine({ r }: { r: InvoiceRow }) {
  return (
    <tr>
      <td className="ob-dtable__grow"><span className="ob-dtable__name" title={r.number ?? "Without a number"}>{r.number ?? "Without a number"}</span></td>
      <td className="ob-dtable__muted">{formatDate(r.createdAt)}</td>
      <td className="ob-dtable__num">{amount(r)}</td>
      <td className="ob-dtable__col--phone">{r.status === "paid" ? STATUS_WORDS.paid : <Pill>{STATUS_WORDS[r.status]}</Pill>}</td>
      <td className="ob-dtable__num">
        {r.hostedUrl && <a className="ob-link" href={r.hostedUrl} target="_blank" rel="noopener noreferrer">{r.status === "open" ? "Pay" : "View"}</a>}
        {r.hostedUrl && r.pdfUrl && " · "}
        {r.pdfUrl && <a className="ob-link" href={r.pdfUrl} target="_blank" rel="noopener noreferrer">PDF</a>}
      </td>
    </tr>
  );
}

/** a Stripe tab while its read is on its way (polish round two): grey, still lines, no "Loading…" */
const TabSkeleton = () => <div className="ob-skeleton-lines" aria-busy="true"><Skeleton shape="line" width="50%" /><Skeleton shape="line" /><Skeleton shape="line" width="70%" /></div>;
